import type { ChoiceCardOption } from '@/components/organisms/FishGroupForms';
import type { SiteTimeline, TimelineFishGroup } from '@/domain/fishGroups';

import { currentStay, movableBioscopeIds } from './groupFacts';
import type { Translate } from './penOptions';
import type { SitePen } from './sitePens';

/** Why a group at the site can't join this merge. */
export type MergeBlock = 'otherStocking' | 'otherSpecies' | 'notStocked';

export interface MergeCandidate {
    fishGroupId: number;
    name: string;
    pen: SitePen;
    stockingCycleId: number | null;
    /** Its scope's timeline has loaded; until then its count and weight aren't known yet. */
    loaded: boolean;
    /** The latest estimate; null when the group has none, or before it has loaded. */
    fishCount: number | null;
    /** Licensed grams; null without a licensed estimate. */
    grams: number | null;
    bioscopeIds: number[];
    /** The group the dialog was opened from: always in, never unticked. */
    initial: boolean;
    detail: TimelineFishGroup | null;
}

/**
 * Every live group at the site, the dialog's group first, with what the board's timelines say
 * about it. A group of another scope is read from that scope's timeline.
 */
export function mergeCandidates(
    group: TimelineFishGroup,
    pens: readonly SitePen[],
    timelines: readonly SiteTimeline[],
    /** The merge's date: a bioscope booked to start later can't be moved in at it. */
    at: Date = new Date()
): MergeCandidate[] {
    const details = new Map(timelines.flatMap((timeline) => timeline.groups).map((g) => [g.id, g]));
    details.set(group.id, group);
    return pens
        .flatMap((pen) => (pen.occupant ? [{ pen, occupant: pen.occupant }] : []))
        .map(({ pen, occupant }) => {
            const detail = details.get(occupant.fishGroupId) ?? null;
            const weight = detail?.weight;
            return {
                fishGroupId: occupant.fishGroupId,
                name: occupant.fishGroupName,
                pen,
                stockingCycleId:
                    occupant.fishGroupId === group.id
                        ? group.stockingCycleId
                        : occupant.stockingCycleId,
                loaded: detail !== null,
                fishCount: detail?.estimatedCount ?? null,
                grams: weight?.status === 'available' ? (weight.livingWeight ?? null) : null,
                bioscopeIds: detail ? movableBioscopeIds(currentStay(detail), at) : pen.bioscopeIds,
                initial: occupant.fishGroupId === group.id,
                detail,
            };
        })
        .sort((a, b) => Number(b.initial) - Number(a.initial));
}

/** The stocking a merge's result joins: the one stocking among the groups in it, if any. */
export function mergeStocking(selected: readonly MergeCandidate[]): number | null {
    return (
        selected.find((candidate) => candidate.stockingCycleId !== null)?.stockingCycleId ?? null
    );
}

/**
 * Why a candidate can't be ticked: another species, another stocking than the one the merge
 * already joins (both 409 on the backend), or a group registered for a later date.
 */
export function mergeBlock(
    candidate: MergeCandidate,
    group: TimelineFishGroup,
    stocking: number | null,
    now: Date
): MergeBlock | null {
    if (candidate.initial) return null;
    if (candidate.detail && candidate.detail.fishType.id !== group.fishType.id) {
        return 'otherSpecies';
    }
    if (
        stocking !== null &&
        candidate.stockingCycleId !== null &&
        candidate.stockingCycleId !== stocking
    ) {
        return 'otherStocking';
    }
    const stay = candidate.detail ? currentStay(candidate.detail) : null;
    if (stay && stay.from > now) return 'notStocked';
    return null;
}

/** The merged group's count, and its mean weight weighted by count, when every part is known. */
export function mergeTotals(selected: readonly MergeCandidate[]): {
    fishCount: number | null;
    grams: number | null;
} {
    if (selected.some((candidate) => candidate.fishCount === null)) {
        return { fishCount: null, grams: null };
    }
    const fishCount = selected.reduce((sum, candidate) => sum + (candidate.fishCount ?? 0), 0);
    if (fishCount === 0 || selected.some((candidate) => candidate.grams === null)) {
        return { fishCount, grams: null };
    }
    const mass = selected.reduce(
        (sum, candidate) => sum + (candidate.fishCount ?? 0) * (candidate.grams ?? 0),
        0
    );
    return { fishCount, grams: mass / fishCount };
}

/** Where the fish end up: a pen of one of the groups merged, or an empty pen. */
export function mergeDestinationOptions(
    pens: readonly SitePen[],
    selected: readonly MergeCandidate[],
    t: Translate
): { value: string; label: string }[] {
    const sources = new Map(selected.map((candidate) => [candidate.pen.cageId, candidate]));
    return pens
        .filter((pen) => sources.has(pen.cageId) || pen.occupant === null)
        .map((pen) => {
            const source = sources.get(pen.cageId);
            return {
                value: String(pen.cageId),
                label: source
                    ? t('siteActions.merge.penSource', { pen: pen.displayName, name: source.name })
                    : t('siteActions.split.penEmpty', { pen: pen.displayName }),
            };
        });
}

/**
 * Which bioscopes measure the merged group: the destination pen's own, plus at most one ticked
 * pen's moved in beside them (a pen can hold several, and measurements follow the pen, so the
 * destination's own can't be unlinked while they stay in it). The others stay in their pens.
 */
export function mergeBioscopeOptions(
    destination: SitePen | undefined,
    selected: readonly MergeCandidate[],
    t: Translate
): ChoiceCardOption[] {
    const keep: ChoiceCardOption = {
        value: 'keep',
        label:
            destination && destination.bioscopeIds.length > 0
                ? t('siteActions.merge.bioscopeDestinationOnly', {
                      ids: destination.bioscopeIds.join(', '),
                      pen: destination.displayName,
                  })
                : t('siteActions.merge.bioscopeNone', { pen: destination?.displayName ?? '' }),
    };
    const moves = selected
        .filter(
            (candidate) =>
                candidate.pen.cageId !== destination?.cageId && candidate.bioscopeIds.length > 0
        )
        .map((candidate) => ({
            value: String(candidate.pen.cageId),
            label:
                destination && destination.bioscopeIds.length > 0
                    ? t('siteActions.merge.bioscopeAlsoFrom', {
                          ids: candidate.bioscopeIds.join(', '),
                          from: candidate.pen.displayName,
                          pen: destination.displayName,
                          others: destination.bioscopeIds.join(', '),
                      })
                    : t('siteActions.merge.bioscopeFrom', {
                          ids: candidate.bioscopeIds.join(', '),
                          from: candidate.pen.displayName,
                          pen: destination?.displayName ?? '',
                      }),
        }));
    return [keep, ...moves];
}
